import { GuidanceResponse, type IGuidanceResponse, type Task } from '@core/fhir-r4/fhir';
import {
  PreTriageGatewayError,
  type PreTriageGateway,
  type PreTriageGatewayBundle,
  type PreTriageGatewayBundleResource,
  type PreTriageGatewayResult,
} from '@core/ports/PreTriageGateway';
import { AuthenticatedBffTransport } from '@infrastructure/http/AuthenticatedBffTransport';
import { BffTransportError } from '@infrastructure/http/BffTransportError';
import { hasTransientRetryStop } from '@infrastructure/http/retryOutcomeHeaders';

interface ProblemDetailsResponse {
  detail?: string;
  title?: string;
}

interface FhirBundleEntry {
  resource?: PreTriageGatewayBundleResource;
}

interface FhirBundle extends PreTriageGatewayBundle {
  entry?: FhirBundleEntry[];
}

interface FhirGuidanceResponsePayload extends Partial<IGuidanceResponse> {
  resourceType?: 'GuidanceResponse';
  [key: string]: unknown;
}

export class BffPreTriageHttpGateway implements PreTriageGateway {
  private static readonly offlineMessage = 'Your internet connection appears to be unavailable. Reconnect to the internet, then start again from the NHS App if you still need help.';

  private static readonly sessionRequiredMessage = 'Start this journey from the NHS App to continue.';

  public constructor(private readonly transport = new AuthenticatedBffTransport()) {}

  public async applyTask(taskPayload: Task): Promise<PreTriageGatewayResult> {
    const response = await this.fetchResponse('/api/triage/PlanDefinition/$apply', {
      method: 'POST',
      headers: {
        Accept: 'application/fhir+json, application/json',
        'Content-Type': 'application/fhir+json',
      },
      body: JSON.stringify(taskPayload),
    });

    if (!response.ok) {
      if (hasTransientRetryStop(response.headers)) {
        throw new PreTriageGatewayError('retry-exhausted', 'Intelligent Navigator could not be reached right now.');
      }

      throw new PreTriageGatewayError('request-failed', await this.buildRequestFailedMessage(response));
    }

    const payload = await response.json() as unknown;
    const gatewayResult = this.extractGatewayResult(payload);

    if (!gatewayResult) {
      throw new PreTriageGatewayError(
        'request-failed',
        'The triage response could not be interpreted as a GuidanceResponse.',
      );
    }

    return gatewayResult;
  }

  private extractGatewayResult(payload: unknown): PreTriageGatewayResult | null {
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    const candidate = payload as FhirGuidanceResponsePayload;
    if (candidate.resourceType === 'GuidanceResponse') {
      return {
        guidanceResponse: new GuidanceResponse(candidate),
      };
    }

    const bundle = payload as FhirBundle;
    if (bundle.resourceType !== 'Bundle' || !Array.isArray(bundle.entry)) {
      return null;
    }

    const guidanceResponsePayload = this.extractBundleGuidanceResponsePayload(bundle);
    if (!guidanceResponsePayload) {
      return null;
    }

    return {
      guidanceResponse: new GuidanceResponse(guidanceResponsePayload),
      bundle,
    };
  }

  private extractBundleGuidanceResponsePayload(bundle: FhirBundle): FhirGuidanceResponsePayload | null {
    if (!Array.isArray(bundle.entry)) {
      return null;
    }

    for (const entry of bundle.entry) {
      if (!entry?.resource || typeof entry.resource !== 'object') {
        continue;
      }

      const resource = entry.resource as FhirGuidanceResponsePayload;
      if (resource.resourceType === 'GuidanceResponse') {
        return resource;
      }
    }

    return null;
  }

  private async fetchResponse(input: RequestInfo | URL, init: RequestInit): Promise<Response> {
    let response: Response;

    try {
      response = await this.transport.fetch(input, init);
    } catch (error) {
      if (error instanceof BffTransportError && error.code === 'offline') {
        throw new PreTriageGatewayError('offline', BffPreTriageHttpGateway.offlineMessage);
      }

      throw error;
    }

    if (response.status === 401) {
      throw new PreTriageGatewayError('session-required', BffPreTriageHttpGateway.sessionRequiredMessage);
    }

    return response;
  }

  private async buildRequestFailedMessage(response: Response): Promise<string> {
    const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';

    if (contentType.includes('application/problem+json') || contentType.includes('application/json')) {
      try {
        const payload = await response.json() as ProblemDetailsResponse;
        const message = payload.detail?.trim() || payload.title?.trim();

        if (message) {
          return message;
        }
      } catch {
        // Fall back to the generic message when the error payload is invalid.
      }
    }

    return 'Unable to process the triage request.';
  }
}